/* eslint-disable default-case */
import React from 'react';
import { DEFAULT_PERFORMANCE_SETTINGS, PERFORMANCE_SETTINGS_KEYS, PeriodType } from './performanceConstants';
import { MONTH, QUARTER, utcDate, WEEK, YEAR } from './relativeDates';
import { DateType } from '../../../util/format';
import { usePerformanceSettings } from '../../../../data-access/query/performanceSettings';
import { useUpdatePerformanceSettings } from '../../../../data-access/mutation/performanceSettings';

// This regular expression allows:
// - A prefix (type) followed by an underscore.
// - A date with format: YYYY-M or YYYY-MM, optionally followed by -D or -DD.
export const DATE_REGEX = /^(.+)_(\d{4}-\d{1,2}(?:-\d{1,2})?)$/;

/**
 * Creates a date filter function that checks if a date (parsed from a date key) falls within the specified range.
 *
 * @param {Date} start - The start date of the range.
 * @param {Date} end - The end date of the range.
 * @returns {Boolean} returns true if the date key is between start and end.
 */
export const createDateFilter = (start, end) => dateKey => {
  const d = utcDate(dateKey);

  return d >= start && d <= end;
};

export const isWeekOrMonth = period => period === WEEK || period === MONTH;

/**
 * Returns the legend string for the specified series type, period.
 *
 * @param {PeriodType} seriesType - The series type (current, previous, stly).
 * @param {String} period - The selected period (WEEK, MONTH, QUARTER, or YEAR).
 * @param {Object} rangesApi - The ranges API object containing date ranges.
 * @param {Function} dateFormatter - Function that can format a single date or date range
 * @returns {String} The legend string.
 */
export const getLineGraphLegend = (seriesType, period, rangesApi, dateFormatter) => {
  const { currentRange, lastRange, stlyRange } = rangesApi;
  let legendDateString = '';

  switch (seriesType) {
    case PeriodType.CURRENT:
      switch (period) {
        case WEEK: {
          legendDateString = dateFormatter(currentRange.start, { endDate: currentRange.end });
          break;
        }
        case MONTH: {
          legendDateString = dateFormatter(currentRange.start, { type: DateType.MONTH });
          break;
        }
        case QUARTER: {
          const startMonth = dateFormatter(currentRange.start, { type: DateType.MONTH });
          const endMonth = dateFormatter(currentRange.end, { type: DateType.QUARTER });
          legendDateString = `${startMonth} - ${endMonth}`;
          break;
        }
        case YEAR: {
          legendDateString = dateFormatter(currentRange.start, { type: DateType.YEAR });
          break;
        }
      }
      break;
    case PeriodType.LAST:
      switch (period) {
        case WEEK: {
          legendDateString = dateFormatter(lastRange.start, { endDate: lastRange.end });
          break;
        }
        case MONTH: {
          legendDateString = dateFormatter(lastRange.start, { type: DateType.MONTH });
          break;
        }
        case QUARTER: {
          const startMonth = dateFormatter(lastRange.start, { type: DateType.MONTH });
          const endMonth = dateFormatter(lastRange.end, { type: DateType.QUARTER });
          legendDateString = `${startMonth} - ${endMonth}`;
          break;
        }
        case YEAR: {
          legendDateString = dateFormatter(lastRange.start, { type: DateType.YEAR });
          break;
        }
      }
      break;
    case PeriodType.STLY:
      switch (period) {
        case WEEK: {
          legendDateString = dateFormatter(stlyRange.start, { endDate: stlyRange.end });
          break;
        }
        case MONTH: {
          legendDateString = dateFormatter(stlyRange.start, { type: DateType.MONTH });
          break;
        }
        case QUARTER: {
          const startMonth = dateFormatter(stlyRange.start, { type: DateType.MONTH });
          const endMonth = dateFormatter(stlyRange.end, { type: DateType.QUARTER });
          legendDateString = `${startMonth} - ${endMonth}`;
          break;
        }
      }
  }
  return legendDateString;
};

export const useEditPerformanceSettings = ({ currentLocationId, locations, dimensions, performanceEnabled }) => {
  const DEFAULT_SETTINGS = React.useMemo(
    () => ({
      ...DEFAULT_PERFORMANCE_SETTINGS,
      [PERFORMANCE_SETTINGS_KEYS.BREAKDOWN_BY_DIMENSION_ID]: dimensions[0].product_dimension_id,
    }),
    [dimensions],
  );

  const [performanceSettings, setPerformanceSettings] = React.useState(() => {
    const initialPerformanceSettings = {};

    locations.forEach(({ location_id: locationId }) => {
      initialPerformanceSettings[locationId] = DEFAULT_SETTINGS;
    });

    return initialPerformanceSettings;
  });

  const {
    mutate: updatePerformanceSettings,
    isLoading: isUpdatingPerformanceSettings,
  } = useUpdatePerformanceSettings();

  const { isLoading: isLoadingPerformanceSettings } = usePerformanceSettings({
    onSuccess: data => {
      const currentPerformanceSettings = [...data];
      let additions = 0;

      locations.forEach(({ location_id: locationId }) => {
        const locationSettings = currentPerformanceSettings.find(
          ({ location_id: currentId }) => currentId === locationId,
        );

        if (!locationSettings) {
          currentPerformanceSettings.push({
            location_id: locationId,
            performance_settings: DEFAULT_SETTINGS,
          });
          additions += 1;
        }
      });

      if (additions > 0) {
        // Update performance settings on the backend
        updatePerformanceSettings(currentPerformanceSettings);
      }

      const performanceSettingsObject = {};
      currentPerformanceSettings.forEach(({ location_id: locationId, performance_settings: settings }) => {
        performanceSettingsObject[locationId] = settings;
      });

      setPerformanceSettings(performanceSettingsObject);
    },
    enabled: performanceEnabled,
  });

  const [currentPerformanceSettings, setCurrentPerformanceSettings] = React.useState(
    () => performanceSettings[currentLocationId] ?? DEFAULT_SETTINGS,
  );

  React.useEffect(() => {
    setCurrentPerformanceSettings(performanceSettings[currentLocationId] ?? DEFAULT_SETTINGS);
  }, [performanceSettings, currentLocationId, DEFAULT_SETTINGS]);

  const hasPendingChanges = (settings = currentPerformanceSettings) => {
    return JSON.stringify(settings) !== JSON.stringify(performanceSettings[currentLocationId]);
  };

  const saveCurrentPerformanceSettings = settingsToSave => {
    const effectiveSettings = settingsToSave ?? currentPerformanceSettings;

    if (!hasPendingChanges(effectiveSettings)) {
      return;
    }

    const updatedPerformanceSettings = {
      ...performanceSettings,
      [currentLocationId]: effectiveSettings,
    };

    const updatedPerformanceSettingsArray = Object.entries(updatedPerformanceSettings).map(
      ([locationId, settings]) => ({
        location_id: parseInt(locationId, 10),
        performance_settings: settings,
      }),
    );

    updatePerformanceSettings(updatedPerformanceSettingsArray);
    setPerformanceSettings(updatedPerformanceSettings);
  };

  return {
    isLoadingPerformanceSettings,
    performanceSettings,
    currentPerformanceSettings,
    setCurrentPerformanceSettings,
    saveCurrentPerformanceSettings,
    isUpdatingPerformanceSettings,
  };
};
